import React, { useEffect, useState } from 'react';
import {
  TextField,
  Grid,
  Typography,
  MenuItem,
  Box,
  LinearProgress,
  Slide,
  Modal,
  Button,
  Stack,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
} from '@mui/material';
import { roi, s3PresignedUrl } from '../../services/CustomerService';
import { useTheme } from '@emotion/react';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import {
  Facebook,
  Twitter,
  Email,
  WhatsApp,
  House,
  Warehouse,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { number } from 'yup';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CorporateFareRoundedIcon from '@mui/icons-material/CorporateFareRounded';

const ROIForm = ({ onSubmit, formDate, initialData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [loader, setLoader] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [shareLink, setShareLink] = useState('');
  const [data, setData] = useState(null);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(
    initialData || {
      property_type: searchParams.get('property-type') || 'Residential',
      state: searchParams.get('state') || '',
      max_bill: searchParams.get('maxbill') || '',
      min_bill: searchParams.get('minbill') || '',
      load: searchParams.get('load') || '',
      pdf_flag: '0',
      customer_name: searchParams.get('name') || '',
      customer_phone: searchParams.get('number') || '',
      pin_code: searchParams.get('pincode') || '',
      address: '',
      project_value: '',
      lease_security_pct: '',
    },
  );

  const [propertyType, setPropertyType] = useState(
    formData?.property_type || 'Residential',
  );

  useEffect(() => {
    setPropertyType(formData.property_type || 'Residential');
  }, [formData.property_type]);

  useEffect(() => {
    const requiredFields = [
      'property_type',
      'state',
      'max_bill',
      'min_bill',
      'load',
    ];
    const allFieldsPresent = requiredFields.every(
      (field) => formData[field] && formData[field].trim() !== '',
    );

    if (allFieldsPresent) {
      formDate(formData);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'propertyType') {
      const propertyTypeValue = value || 'Residential';
      setPropertyType(propertyTypeValue);
      setFormData((prev) => ({
        ...prev,
        property_type: propertyTypeValue,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formDate) {
      formDate(formData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const dataToSubmit = {
      ...formData,
      property_type: formData.property_type || 'Residential',
    };

    setLoader(true);
    try {
      // Add a delay before calling the API
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const response = await roi(dataToSubmit);

      onSubmit(response);

      if (formDate) {
        formDate(formData);
      }

      setLoader(false);
      setShareLink(response?.pdf_url);
      setData(response);
    } catch (error) {
      console.error('Error submitting data:', error);
      alert('Failed to submit data.');
      setLoader(false);
    }
  };

  const downloadPdf = async () => {
    const pdfUrl = await s3PresignedUrl(data?.pdf_url);
    window.open(pdfUrl?.url);
  };

  const handleCloseAnchor = () => {
    setAnchorEl(null);
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setAnchorEl(null);
  };
  const shareOptions = [
    {
      icon: <Facebook sx={{ mr: '12px' }} />,
      label: 'Facebook',
      url: 'https://www.facebook.com/sharer/sharer.php?u=',
    },
    {
      icon: <Twitter sx={{ mr: '12px' }} />,
      label: 'Twitter',
      url: 'https://twitter.com/intent/tweet?url=',
    },
    {
      icon: <Email sx={{ mr: '12px' }} />,
      label: 'Email',
      url: 'mailto:?body=',
    },
    {
      icon: <WhatsApp sx={{ mr: '12px' }} />,
      label: 'WhatsApp',
      url: 'https://api.whatsapp.com/send?text=',
    },
  ];

  return (
    <>
      <Box sx={{ maxWidth: 600, margin: '0 auto', padding: 2 }}>
        <Typography variant='h5' mb={4} textAlign={'center'} fontWeight='bold'>
          Solar return on investment
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component='fieldset' sx={{ mb: 1, width: '100%' }}>
                <ToggleButtonGroup
                  size='small'
                  value={propertyType}
                  defaultValue='Residential'
                  exclusive
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: 'propertyType',
                        value: newValue || 'Residential',
                      },
                    });
                  }}
                  sx={{
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: '1px solid #ccc',
                  }}
                >
                  {['Residential', 'Commercial'].map((type) => (
                    <ToggleButton
                      key={type}
                      value={type}
                      sx={{
                        flex: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 0,
                        backgroundColor:
                          propertyType === type
                            ? theme.palette.primary.main
                            : 'white',
                        color:
                          propertyType === type
                            ? 'white'
                            : theme.palette.primary.main,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          '&:hover': { backgroundColor: 'darkgreen' },
                        },
                      }}
                    >
                      {'Residential' === type ? (
                        <House sx={{ fontSize: '22px', paddingRight: '5px' }} />
                      ) : (
                        <CorporateFareRoundedIcon
                          sx={{ fontSize: '22px', paddingRight: '5px' }}
                        />
                      )}
                      {type}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size='small'
                fullWidth
                select
                label='State'
                name='state'
                value={formData.state}
                onChange={handleChange}
              >
                <MenuItem value='Punjab'>Punjab</MenuItem>
                <MenuItem value='Uttar Pradesh'>Uttar Pradesh</MenuItem>
                <MenuItem value='Delhi'>Delhi</MenuItem>
                <MenuItem value='Karnataka'>Karnataka</MenuItem>
                <MenuItem value='Haryana'>Haryana</MenuItem>
                <MenuItem value='Haryana'>Bihar</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size='small'
                fullWidth
                label='Load'
                name='load'
                type='numeric'
                value={formData.load}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size='small'
                fullWidth
                label='Max bill'
                name='max_bill'
                type='numeric'
                value={formData.max_bill}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size='small'
                fullWidth
                label='Min bill'
                name='min_bill'
                type='numeric'
                value={formData.min_bill}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack gap={2} flexDirection={'row'} justifyContent={'flex-end'}>
                <Button
                  disabled={loader}
                  type='submit'
                  variant='contained'
                  color='primary'
                  startIcon={
                    loader ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : (
                      <BoltRoundedIcon />
                    )
                  }
                >
                  Get estimates
                  {loader && <LinearProgress />}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
};

export default ROIForm;
